import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Input } from '../components/ui/input';
import { CloudRain, Phone, User, Mail, MapPin, AlertTriangle, CheckCircle2, Camera, ShieldCheck, Clock, FileText, Droplets, Home } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getCitta, getLeadTrackingFields } from '../utils/leadTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const checks = [
  { icon: Home, title: 'Tegole e coppi', desc: 'Crepe e microfratture invisibili da terra causate dagli impatti della grandine.' },
  { icon: Droplets, title: 'Guaine e sigillature', desc: 'Punti di infiltrazione su comignoli, lucernari e converse.' },
  { icon: CloudRain, title: 'Grondaie e pluviali', desc: 'Ostruzioni e distacchi che fanno tracimare l\u2019acqua sotto il manto.' },
  { icon: Camera, title: 'Report fotografico', desc: 'Documentazione completa dei danni, utile anche per l\u2019assicurazione.' },
];

const EmergenzaMeteoPage = () => {
  const navigate = useNavigate();
  const citta = getCitta();
  const [form, setForm] = useState({ name: '', phone: '', email: '', comune: citta, website: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.comune) { toast.error('Compila tutti i campi'); return; }
    setIsSubmitting(true);
    try {
      const tracking = getLeadTrackingFields();
      await axios.post(`${BACKEND_URL}/api/leads`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        province: 'Da definire',
        houseType: 'Da definire',
        ownershipStatus: 'Da definire',
        problemDescription: `EMERGENZA METEO | Verifica danni grandine/pioggia | Comune: ${form.comune}`,
        tipoIntervento: 'Emergenza Meteo - Verifica danni',
        website: form.website,
        ...tracking,
        citta: form.comune,
      });
      if (typeof window.gtag === 'function') window.gtag('event', 'conversion', { send_to: 'AW-17960264240/HNy_CMqrr_obELDEj_RC', value: 1.0, currency: 'EUR' });
      if (typeof window.fbq === 'function') window.fbq('track', 'Lead', { content_name: 'Emergenza Meteo' });
      navigate('/grazie');
    } catch { toast.error("Errore nell'invio. Riprova."); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="emergenza-meteo-page">
      {/* Slim header */}
      <header className="bg-[#0F172A] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" data-testid="meteo-logo-link">
            <img src="https://customer-assets.emergentagent.com/job_smart-roofing-2/artifacts/7ngvxkwz_2.png" alt="Evoluzione Tetto" className="h-8 w-auto" />
          </Link>
          <a href="tel:800864621" data-testid="meteo-header-phone" className="flex items-center gap-2 text-white font-bold hover:text-emerald-400 transition-colors">
            <Phone className="w-4 h-4 text-emerald-400" />
            <span className="hidden sm:inline text-sm font-normal text-slate-400">Numero Verde</span> 800 86 46 21
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-[#0F172A] overflow-hidden pt-12 pb-20" data-testid="meteo-hero">
        <div className="absolute inset-0">
          <img src="https://static.prod-images.emergentagent.com/jobs/b2eca1db-26c1-43d9-9c75-7df69e4d27a2/images/89bc83e184c08dc1d1c42b1097952a68aa39ab70f4fc9d3e51a1f37606889190.png" alt="" className="w-full h-full object-cover opacity-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-red-950/40 via-[#0F172A]/60 to-[#0F172A]" />
        </div>
        <div className="max-w-6xl mx-auto px-6 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-7 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 mb-5 px-4 py-2 bg-red-500/15 border border-red-500/30 rounded-full animate-pulse">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-red-400 font-semibold text-sm uppercase tracking-wide">Allerta Meteo — Verifiche in corso</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5 leading-[1.08] text-white">
              Grandine o pioggia forte{citta ? <> a <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400">{citta}</span></> : ''}?
            </h1>
            <p className="text-lg text-slate-300 mb-6 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              I danni al tetto <strong className="text-white">non si vedono da terra</strong>: tegole crepate e guaine lesionate fanno entrare l'acqua alla prossima pioggia, quando il danno costa 10 volte tanto.
            </p>
            <div className="space-y-2.5 max-w-md mx-auto lg:mx-0 text-left">
              {['Verifica gratuita del tetto entro 24 ore', 'Report fotografico utile per l\u2019assicurazione', 'Senza ponteggi: intervento rapido e senza stress'].map((t) => (
                <div key={t} className="flex items-center gap-2.5 text-slate-200 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" /><span>{t}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="lg:col-span-5">
            <div className="bg-white rounded-2xl p-7 shadow-2xl">
              <div className="text-center mb-5">
                <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Richiedi la Verifica Gratuita</h2>
                <p className="text-slate-500 text-sm mt-1">Le richieste vengono gestite in ordine di arrivo.</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3.5" data-testid="meteo-form">
                <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }} />
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input data-testid="meteo-form-name" type="text" placeholder="Il tuo nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent" />
                </div>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input data-testid="meteo-form-phone" type="tel" placeholder="Numero di telefono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent" />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input data-testid="meteo-form-email" type="email" placeholder="La tua email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent" />
                </div>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input data-testid="meteo-form-comune" type="text" placeholder="Comune colpito (es. Verona)" value={form.comune} onChange={(e) => setForm({ ...form, comune: e.target.value })} required className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent" />
                </div>
                <button data-testid="meteo-form-submit" type="submit" disabled={isSubmitting} className="w-full bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-bold text-base py-3.5 rounded-xl transition-all duration-300 shadow-[0_4px_20px_rgba(220,38,38,0.35)] hover:-translate-y-0.5">
                  {isSubmitting ? 'Invio in corso...' : 'Verifica Gratuita in 24h'}
                </button>
              </form>
              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> 100% gratuito e senza impegno — dati protetti (GDPR)
              </div>
              <div className="mt-5 pt-4 border-t border-slate-100 text-center">
                <p className="text-xs text-slate-400 mb-0.5">Preferisci chiamare subito?</p>
                <a href="tel:800864621" data-testid="meteo-form-phone-cta" className="text-lg font-bold text-red-600 hover:text-red-500 transition-colors">800 86 46 21</a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-14 bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: FileText, step: '1', title: 'Invia la richiesta', desc: 'Compila il modulo o chiama il numero verde: bastano 30 secondi.' },
            { icon: Clock, step: '2', title: 'Tecnico entro 24h', desc: 'Un tecnico certificato ispeziona il tetto in sicurezza, senza ponteggi.' },
            { icon: Camera, step: '3', title: 'Report + preventivo', desc: 'Ricevi foto dei danni e un preventivo chiaro. Decidi tu, senza impegno.' },
          ].map((s) => (
            <div key={s.step} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-red-600 text-white font-bold flex items-center justify-center">{s.step}</div>
                <s.icon className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-1.5" style={{ fontFamily: 'Outfit' }}>{s.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Cosa controlliamo */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-red-600 text-sm font-semibold uppercase tracking-wider">Ispezione completa</span>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-2">Cosa controlliamo dopo una grandinata</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {checks.map((c) => (
              <div key={c.title} className="flex gap-4 p-5 rounded-2xl border border-slate-200 bg-white hover:shadow-md transition-shadow">
                <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center shrink-0"><c.icon className="w-5 h-5 text-red-600" /></div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>{c.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{c.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Assicurazione callout */}
      <section className="pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-[#0F172A] rounded-2xl p-8 md:p-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1.5 bg-emerald-500/15 border border-emerald-500/25 rounded-full">
                <ShieldCheck className="w-4 h-4 text-emerald-400" /><span className="text-emerald-400 text-xs font-semibold uppercase">Lo sapevi?</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>I danni da grandine sono spesso coperti dall'assicurazione casa</h3>
              <p className="text-slate-300 text-sm leading-relaxed">Molte polizze includono gli eventi atmosferici. Il nostro report fotografico è la documentazione che serve per aprire la pratica: <strong className="text-white">in molti casi la riparazione la paga l'assicurazione</strong>.</p>
            </div>
            <div className="md:col-span-4 text-center">
              <a href="tel:800864621" data-testid="meteo-cta-phone" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-bold px-6 py-3.5 rounded-xl transition-all hover:-translate-y-0.5 shadow-lg">
                <Phone className="w-4 h-4" /> 800 86 46 21
              </a>
              <p className="text-slate-400 text-xs mt-2">Numero Verde Gratuito</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mini footer */}
      <footer className="bg-[#0F172A] py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400">
          <p>© {new Date().getFullYear()} Revogreen Srl · Via Dominutti 6, 37135 Verona</p>
          <div className="flex gap-5">
            <Link to="/" className="hover:text-white transition-colors">Sito principale</Link>
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/cookie-policy" className="hover:text-white transition-colors">Cookie Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EmergenzaMeteoPage;
